/**
 * The fleet roster (docs/ux.md §10.1): own ships with what each can still do, filters, and a
 * way to the opponent's fleet. A row selects its ship (the page centres the map on it); the
 * squadron itself is chosen on the map (U-MUST-01), the roster only shows it.
 */
import type { TFunction } from 'i18next';
import { SHIP_CLASS_IDS, fireTargets, type GameState, type ShipState, type Side } from '@brig/rules-engine';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NationFlag } from '../nations/NationFlag.tsx';
import { ShipToken } from '../ships/ShipToken.tsx';
import { tokenStateOf } from '../ships/shipState.ts';
import * as styles from './Game.css.ts';
import { current } from './history.ts';
import { Icon } from './icons.tsx';
import { unitLabel } from './names.ts';
import { uiPhase } from './phase.ts';
import { SideMark } from './TurnControls.tsx';
import { inPortCell, shipsToMove, squadronCandidates } from './turn.ts';
import { liveSquadron } from './turnMachine.ts';
import type { TurnSend, TurnSnapshot } from './useTurn.ts';

type Filter = 'canAct' | 'all' | 'squadron' | 'damaged' | 'inPort';
const FILTERS: readonly Filter[] = ['canAct', 'all', 'squadron', 'damaged', 'inPort'];

type Chip = { readonly key: string; readonly text: string; readonly tone?: 'good' | 'bad' };

function chips(
  t: TFunction,
  state: GameState,
  ship: ShipState,
  squadron: ReadonlySet<string>,
  toMove: ReadonlySet<string>,
): Chip[] {
  if (ship.sunk) return [{ key: 'sunk', text: t('game.roster.status.sunk'), tone: 'bad' }];
  const own = ship.side === state.turn.side;
  const out: Chip[] = [];
  if (own && (squadron.has(ship.id) || ship.inGroup))
    out.push({ key: 'sq', text: t('game.roster.status.inSquadron'), tone: 'good' });
  if (own && toMove.has(ship.id))
    out.push({ key: 'move', text: t('game.roster.status.toMove'), tone: 'good' });
  else if (own && ship.moved && ship.dockedOnTurn === null)
    out.push({ key: 'moved', text: t('game.roster.status.moved') });
  if (own && ship.fired) out.push({ key: 'fired', text: t('game.roster.status.fired') });
  if (ship.shotsLeft > 0 && own && fireTargets(state, ship.id).length > 0) {
    out.push({ key: 'shots', text: t('game.roster.status.shots', { n: ship.shotsLeft }), tone: 'good' });
  }
  if (ship.dockedOnTurn !== null)
    out.push({ key: 'dock', text: t('game.roster.status.dock', { n: ship.damage }) });
  if (ship.repairingFort !== null) out.push({ key: 'crew', text: t('game.roster.status.crew') });
  else if (ship.dockedOnTurn === null && inPortCell(state, ship.id))
    out.push({ key: 'port', text: t('game.roster.status.inPort') });
  return out;
}

function matches(
  filter: Filter,
  state: GameState,
  ship: ShipState,
  list: ReadonlySet<string>,
  squadron: ReadonlySet<string>,
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'canAct':
      return list.has(ship.id);
    case 'squadron':
      return squadron.has(ship.id) || ship.inGroup;
    case 'damaged':
      return ship.damage > 0 && !ship.sunk;
    case 'inPort':
      return !ship.sunk && inPortCell(state, ship.id);
  }
}

/**
 * A compact list (spec v2 §15): each ship's miniature, hull, shots and whether it can still sail,
 * its cell, and a button to look at it. Filter chips with counts above. A row selects its ship
 * (the page centres the map on it). The list only looks: the squadron is chosen on the map
 * (U-MUST-01), and rows say who is in it. `onPicked` runs after a row selected its ship, e.g. to
 * fold the sheet away on a phone so the map shows it.
 */
export function FleetRoster({
  snapshot,
  send,
  onPicked,
  onCentre,
}: {
  snapshot: TurnSnapshot;
  send: TurnSend;
  onPicked?: () => void;
  onCentre?: (cell: string) => void;
}) {
  const { t } = useTranslation();
  const state = current(snapshot.context.history);
  const own = state.turn.side;
  const [side, setSide] = useState<'mine' | 'theirs'>('mine');
  const [filter, setFilter] = useState<Filter>('canAct');
  const shown: Side = side === 'mine' ? own : own === 'red' ? 'blue' : 'red';
  const phase = uiPhase(snapshot);
  const mustering = phase === 'muster';
  const squadron = new Set(liveSquadron(snapshot.context));
  const declarable = new Set(squadronCandidates(state));
  const toMove = new Set(shipsToMove(state));
  const canAct = new Set([
    ...(mustering ? declarable : toMove),
    ...state.ships
      .filter((s) => s.side === own && s.shotsLeft > 0 && !s.sunk && fireTargets(state, s.id).length > 0)
      .map((s) => s.id),
  ]);
  const offered = FILTERS.filter((f) => side === 'mine' || (f !== 'canAct' && f !== 'squadron'));
  const effective: Filter = offered.includes(filter) ? filter : 'all';
  const fleet = state.ships
    .filter((s) => s.side === shown)
    .sort(
      (a, b) =>
        Number(a.sunk) - Number(b.sunk) ||
        SHIP_CLASS_IDS.indexOf(a.cls) - SHIP_CLASS_IDS.indexOf(b.cls) ||
        (a.id < b.id ? -1 : 1),
    );
  const count = (f: Filter) => fleet.filter((s) => matches(f, state, s, canAct, squadron)).length;
  const rows = fleet.filter((s) => matches(effective, state, s, canAct, squadron));
  const afloat = fleet.filter((s) => !s.sunk).length;
  const selected = snapshot.context.selected;
  const nations = snapshot.context.history.nations;

  return (
    <section className={styles.roster} aria-label={t('game.roster.title')}>
      <div className={styles.rosterTabs} role="tablist">
        {(['mine', 'theirs'] as const).map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={side === k}
            className={styles.rosterTab}
            onClick={() => {
              setSide(k);
            }}
          >
            <SideMark snapshot={snapshot} side={k === 'mine' ? own : own === 'red' ? 'blue' : 'red'}>
              {t(k === 'mine' ? 'game.roster.mine' : 'game.roster.theirs')}
            </SideMark>
          </button>
        ))}
      </div>
      <div className={styles.rosterChips} role="group" aria-label={t('game.roster.filter.label')}>
        {offered.map((f) => (
          <button
            key={f}
            type="button"
            className={styles.rosterChip}
            aria-pressed={effective === f}
            onClick={() => {
              setFilter(f);
            }}
          >
            {t(`game.roster.filter.${f}`)} <span className={styles.rosterChipCount}>{count(f)}</span>
          </button>
        ))}
      </div>
      <span className={styles.hint}>{t('game.roster.afloat', { n: afloat, total: fleet.length })}</span>
      {rows.length === 0 ? <p className={styles.hint}>{t('game.roster.none')}</p> : null}
      <ul className={styles.rosterList}>
        {rows.map((ship) => {
          const name = unitLabel(t, state, nations, ship.id);
          const cls = state.ruleset.shipClasses[ship.cls];
          const max = cls.sinksAt;
          const mine = ship.side === own;
          const sails = mine && (mustering ? declarable.has(ship.id) : toMove.has(ship.id));
          const status = chips(t, state, ship, squadron, toMove)
            .map((c) => c.text)
            .join(', ');
          return (
            <li
              key={ship.id}
              className={styles.rosterRow}
              data-selected={selected === ship.id}
              data-sunk={ship.sunk}
              data-squadron={squadron.has(ship.id) || (ship.inGroup && mine)}
            >
              <button
                type="button"
                className={styles.rosterPick}
                aria-pressed={selected === ship.id}
                disabled={ship.sunk}
                aria-label={[name, t('game.unitPanel.holes', { n: ship.damage, max }), ship.cell, status]
                  .filter(Boolean)
                  .join(' · ')}
                data-tip={status || undefined}
                onClick={() => {
                  send({ type: 'SELECT', id: ship.id });
                  onPicked?.();
                }}
              >
                <span className={styles.rosterThumb} aria-hidden="true">
                  <ShipToken
                    type={ship.cls}
                    nation={nations[ship.side]}
                    size={40}
                    state={tokenStateOf(ship, cls)}
                  />
                </span>
                <span className={styles.rosterName}>
                  <NationFlag nation={nations[ship.side]} height={11} decorative />
                  {name}{' '}
                </span>
                <span className={styles.rosterStats}>
                  <span data-icon="hole" data-hit={ship.damage > 0}>
                    <Icon name="hole" size={13} />
                    {ship.damage}/{max}
                  </span>{' '}
                  {mine ? (
                    <span>
                      <Icon name="cannon" size={13} />
                      {ship.shotsLeft}/{cls.shotsPerTurn}
                    </span>
                  ) : null}{' '}
                  {mine ? (
                    <span data-off={!sails}>
                      <Icon name="sail" size={13} />
                      {sails ? '✓' : '–'}
                    </span>
                  ) : null}
                </span>
                <span className={styles.rosterCell}>{ship.cell}</span>
              </button>
              {onCentre && !ship.sunk ? (
                <button
                  type="button"
                  className={styles.rosterCentre}
                  aria-label={`${t('game.unitPanel.centre')}: ${name}`}
                  data-tip={t('game.unitPanel.centre')}
                  onClick={() => {
                    onCentre(ship.cell);
                  }}
                >
                  <Icon name="centre" size={18} />
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
